import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TechLayout from '../../components/tech/TechLayout';
import { getWorkOrders } from '../../api/client';

const TABS = [
  { key: 'assigned', label: 'Assigned' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'all', label: 'All' },
];

const STATUS_COLORS = {
  pending: { bg: '#FEF3C7', color: '#92400E' },
  assigned: { bg: '#DBEAFE', color: '#1E40AF' },
  in_progress: { bg: '#EDE9FE', color: '#5B21B6' },
  completed: { bg: '#D1FAE5', color: '#065F46' },
  cancelled: { bg: '#F3F4F6', color: '#6B7280' },
};

function StatusPill({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.cancelled;

  return (
    <span
      style={{
        ...s,
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {status?.replace('_', ' ') || 'unknown'}
    </span>
  );
}

function JobCard({ job, onView }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: 14,
        padding: 14,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 10,
          alignItems: 'flex-start',
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
            {job.job_number || `WO-${job.id}`}
          </div>
          <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>
            {job.customer_name || 'Unknown Customer'}
          </div>
        </div>

        <StatusPill status={job.status} />
      </div>

      <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
        <div>{job.address || 'No address'}</div>
        <div>{job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : 'No schedule'}</div>
        <div>Priority: {job.priority || 'normal'}</div>
        {job.technician_name && <div>Technician: {job.technician_name}</div>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={onView}
          style={{
            flex: 1,
            border: 'none',
            background: '#2563EB',
            color: '#fff',
            borderRadius: 10,
            padding: '10px 12px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          View Details
        </button>

        <button
          onClick={() => {
            if (job.address) {
              window.open(
                `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.address)}`,
                '_blank'
              );
            }
          }}
          style={{
            flex: 1,
            border: '1px solid #2563EB',
            background: '#fff',
            color: '#2563EB',
            borderRadius: 10,
            padding: '10px 12px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Navigate
        </button>
      </div>
    </div>
  );
}

export default function TechJobs() {
  const navigate = useNavigate();

  const [tab, setTab] = useState('assigned');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser?.id;
  const currentUserRole = currentUser?.role || '';

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);

        const res = await getWorkOrders({});
        const allJobs = res?.data || [];

        console.log('current user:', currentUser);
        console.log('all jobs:', allJobs);

        let jobsToShow = allJobs;

        // technician 只看自己的工单
        if (currentUserRole === 'technician') {
          jobsToShow = allJobs.filter((job) => {
            if (!currentUserId) return false;

            if (job.technician_id === currentUserId) return true;
            if (String(job.technician_id) === String(currentUserId)) return true;

            return false;
          });
        }

        // admin / manager / dispatcher 看全部
        console.log('jobs to show:', jobsToShow);

        if (!mounted) return;
        setJobs(jobsToShow);
      } catch (e) {
        console.error('getWorkOrders failed:', e.response?.data || e);

        if (!mounted) return;
        setJobs([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [currentUserId, currentUserRole]);

  const filteredJobs = useMemo(() => {
    if (tab === 'assigned') return jobs.filter((j) => j.status === 'assigned');
    if (tab === 'in_progress') return jobs.filter((j) => j.status === 'in_progress');
    if (tab === 'completed') return jobs.filter((j) => j.status === 'completed');
    return jobs;
  }, [jobs, tab]);

  return (
    <TechLayout title="My Jobs">
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              border: 'none',
              borderRadius: 999,
              padding: '9px 14px',
              whiteSpace: 'nowrap',
              background: tab === t.key ? '#2563EB' : '#fff',
              color: tab === t.key ? '#fff' : '#374151',
              fontWeight: 700,
              cursor: 'pointer',
              borderColor: '#E5E7EB',
              boxShadow: tab === t.key ? 'none' : 'inset 0 0 0 1px #E5E7EB',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#6B7280', padding: '40px 0' }}>
          Loading jobs...
        </div>
      ) : filteredJobs.length === 0 ? (
        <div
          style={{
            background: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: 14,
            padding: 24,
            textAlign: 'center',
            color: '#6B7280',
          }}
        >
          {currentUserRole === 'technician'
            ? 'No jobs assigned to you.'
            : 'No jobs found.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {filteredJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onView={() => navigate(`/tech/jobs/${job.id}`)}
            />
          ))}
        </div>
      )}
    </TechLayout>
  );
}